import React, { createContext, useContext } from "react"
import styled from "styled-components"
import { Theme } from "@pga/pga-component-library"

import { redirectToMemberApplications } from "../../../lib/ptmApplicationsRedirect"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import { RECIPROCITY_APPLICATION_STATE } from "../../../utils/reciprocityApplication"
import ApplicationDenialMessage, {
  ContactLinks,
} from "../../PtmApplicationStatus/ApplicationDenialMessage"
import { resolveUrlForArticles } from "../MembershipJourney/utils/urlUtils"
import {
  getStatusBackgroundColor,
  getStatusColor,
} from "../MembershipJourney/utils/displayUtils"
import {
  ActionButton,
  ActionButtonText,
  RequirementActions,
  RequirementDescription,
  RequirementHeader,
  RequirementTitle,
  StatusBadgeCheckmark,
  StatusBadgeSmall,
  StatusBadgeText,
  TextButton,
} from "../MembershipJourney/Styles/ProgressTracker.styles"
import { Title } from "../Prospect/PathBox/styles"
import {
  RECIPROCITY_APPLICATION_TITLE,
  reciprocityApplicationCopy,
} from "./copy"

const ReciprocityApplicationContext = createContext(null)

export const ReciprocityApplicationProvider =
  ReciprocityApplicationContext.Provider

/** The candidate's application the dashboard shows instead of the apply action, or null. */
export const useReciprocityApplicationState = () =>
  useContext(ReciprocityApplicationContext)

const ReciprocityApplicationLoadingContext = createContext(false)

export const ReciprocityApplicationLoadingProvider =
  ReciprocityApplicationLoadingContext.Provider

/** True while the eligibility or application request is still in flight. */
export const useReciprocityApplicationLoading = () =>
  useContext(ReciprocityApplicationLoadingContext)

const Entry = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-bottom: 20px;
  padding: 1rem 1.25rem;
  background-color: white;
  border-left: 4px solid ${Theme.colors.primary};
`

const Panel = styled.section`
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 12px;
  padding: 20px 1.5rem;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const ContactLine = styled.div`
  color: #486280;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
`

const ApplicationStatusContent = ({ application }) => {
  const [denialMessageEnabled] =
    useFeatureFlags([flags.FLAG_FEAT_APPLICATIONS_DENIAL_MESSAGE]) ?? []
  const copy = reciprocityApplicationCopy(application)
  if (!copy) return null

  const isElected = application.state === RECIPROCITY_APPLICATION_STATE.ELECTED
  const isDenied = application.state === RECIPROCITY_APPLICATION_STATE.DENIED

  return (
    <Content data-testid="reciprocity-application-status">
      <RequirementHeader>
        <RequirementTitle>{RECIPROCITY_APPLICATION_TITLE}</RequirementTitle>
        <StatusBadgeSmall
          backgroundColor={getStatusBackgroundColor(copy.statusKey)}
        >
          {isElected ? (
            <StatusBadgeCheckmark color={getStatusColor(copy.statusKey)} />
          ) : null}
          <StatusBadgeText textColor={getStatusColor(copy.statusKey)}>
            {copy.label}
          </StatusBadgeText>
        </StatusBadgeSmall>
      </RequirementHeader>
      <RequirementDescription>{copy.message}</RequirementDescription>
      {isDenied && denialMessageEnabled && application.applicationId ? (
        <ApplicationDenialMessage
          applicationId={application.applicationId}
          applicationType="member"
        />
      ) : null}
      {copy.showContact ? (
        <ContactLine>
          Membership Services: <ContactLinks />.
        </ContactLine>
      ) : null}
      {copy.primaryActionText || copy.secondaryLinkText ? (
        <RequirementActions>
          {copy.primaryActionText ? (
            <ActionButton type="button" onClick={redirectToMemberApplications}>
              <ActionButtonText>{copy.primaryActionText}</ActionButtonText>
            </ActionButton>
          ) : null}
          {copy.secondaryLinkText ? (
            <TextButton
              as="a"
              href={resolveUrlForArticles(copy.secondaryLinkUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {copy.secondaryLinkText}
            </TextButton>
          ) : null}
        </RequirementActions>
      ) : null}
    </Content>
  )
}

/**
 * Takes the place of the Path to Membership widget on the prospect dashboard
 * for a reciprocity candidate who has started an application, so they see the
 * application's progress and not the Associate or Affiliate chooser.
 */
export const ReciprocityApplicationPanel = ({ application }) => {
  if (!application) return null

  return (
    <Panel aria-label="Membership application">
      <Title>Path to Membership</Title>
      <ApplicationStatusContent application={application} />
    </Panel>
  )
}

/** Standalone card above every other dashboard shell. */
const ReciprocityApplicationEntry = ({ application }) => {
  if (!application) return null

  return (
    <Entry aria-label="Membership application">
      <ApplicationStatusContent application={application} />
    </Entry>
  )
}

export default ReciprocityApplicationEntry
